"use client"

import { useState } from "react"
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import { CloudUpload, Send, CheckCircle } from "@mui/icons-material"
import axios from "axios"
import * as XLSX from "xlsx"

const BulkOrderAgentUpdate = () => {
  const [file, setFile] = useState(null)
  const [fileName, setFileName] = useState("")
  const [loading, setLoading] = useState(false)
  const [parsedData, setParsedData] = useState(null)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  })

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    const droppedFile = e.dataTransfer.files[0]
    handleFile(droppedFile)
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    handleFile(selectedFile)
  }

  const handleFile = (selectedFile) => {
    if (
      selectedFile &&
      (selectedFile.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        selectedFile.type === "application/vnd.ms-excel")
    ) {
      setFile(selectedFile)
      setFileName(selectedFile.name)
      parseExcel(selectedFile)
    } else {
      setSnackbar({
        open: true,
        message: "Please upload an Excel file (.xlsx or .xls)",
        severity: "error",
      })
    }
  }

  const parseExcel = (file) => {
    setLoading(true)
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: "array" })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet)

        // Extract order IDs and agent IDs
        const orderIds = []
        const agentIds = []

        jsonData.forEach((row) => {
          // Assuming the Excel has columns named "orderId" and "agentId"
          // Adjust these keys based on your actual Excel column names
          if (row.orderId) orderIds.push(row.orderId)
          if (row.agentId) agentIds.push(row.agentId)
        })

        const formattedData = {
          ids: orderIds,
          agentIds: agentIds,
        }

        setParsedData(formattedData)
        setLoading(false)

        setSnackbar({
          open: true,
          message: "File parsed successfully!",
          severity: "success",
        })
      } catch (error) {
        console.error("Error parsing Excel file:", error)
        setLoading(false)
        setSnackbar({
          open: true,
          message: "Error parsing Excel file. Please check the format.",
          severity: "error",
        })
      }
    }

    reader.readAsArrayBuffer(file)
  }

  const handleSubmit = async () => {
    if (!parsedData || parsedData.ids.length === 0 || parsedData.agentIds.length === 0) {
      setSnackbar({
        open: true,
        message: "No valid data to submit",
        severity: "error",
      })
      return
    }

    setLoading(true)

    try {
      const response = await axios.post(process.env.REACT_APP_PATH_BULK+"/testing/updateBulkOrderAgents", parsedData)
      if(response.data.success){

        setSnackbar({
          open: true,
          message: "Data submitted successfully!",
          severity: "success",
        })
        // Reset form after successful submission
        setFile(null)
        setFileName("")
        setParsedData(null)
      }else{
        setSnackbar({
          open: true,
          message: response.data?.message||'Something went wrong',
          severity: "error",
        })
      }
    } catch (error) {
      console.error("Error submitting data:", error)
      setSnackbar({
        open: true,
        message: `Error submitting data: ${error.response?.data?.message || error.message}`,
        severity: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  return (
    <Box sx={{ maxWidth: 800, margin: "0 auto", padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        Bulk Order Agent Update
      </Typography>

      <Paper
        sx={{
          padding: 3,
          marginBottom: 3,
          border: "2px dashed #ccc",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 200,
          cursor: "pointer",
        }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => document.getElementById("file-input").click()}
      >
        <input
          id="file-input"
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        <CloudUpload sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />

        {fileName ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircle color="success" />
            <Typography>{fileName}</Typography>
          </Box>
        ) : (
          <Typography>Drag and drop an Excel file here, or click to select</Typography>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          File should contain columns for orderId and agentId
        </Typography>
      </Paper>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      <Button
        variant="contained"
        color="primary"
        size="large"
        startIcon={<Send />}
        onClick={handleSubmit}
        disabled={!parsedData || loading}
        fullWidth
      >
        Submit Data
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default BulkOrderAgentUpdate
